import { useCallback, useContext, useEffect, useRef } from 'react'
import { v4 as randomUUID } from 'uuid'
import { useAuth0 } from '@/auth/index'
import { useAwsCredentialsManager } from '@/routes/plus/context/AWS-credentials.context' // Asumiendo ruta correcta
import {
  type IncomingMessage,
  type Message,
  MessageSender,
  type OutgoingMessage,
} from '@/routes/plus/models/Conversation.model' // Asumiendo rutas correctas
import { Context } from '@/shared/store/reducers/global-reducer'
import useConversationsStore from '@/stores/useConversationsStore'

import { getConfig } from '@/config/runtime'

export const useSocket = (options?: {
  onConversationCreated?: (conversationId: string) => void
}) => {
  const socketRef = useRef<WebSocket | null>(null)
  const addMessage = useConversationsStore((s) => s.addMessage)
  const createConversation = useConversationsStore((s) => s.createConversation)
  const setAwaitingResponse = useConversationsStore((s) => s.setAwaitingResponse)

  const {
    getValidCredentials,
    isLoading: isLoadingCredentials,
    error: credentialsError,
    credentials: currentAwsCredentials, // Se mantiene para referencia si es necesario, pero getValidCredentials es la fuente principal
    applicationId,
    isAuthorized,
    hasAuth0TokenBeenSuccessfullyUsed, // Nombre actualizado de la variable
  } = useAwsCredentialsManager()

  const { user } = useAuth0()
  const { store } = useContext(Context)
  const { activeTenant } = store

  const connect = useCallback((): WebSocket => {
    if (
      socketRef.current &&
      (socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return socketRef.current
    }

    console.log('Intentando conectar WebSocket...')
    const ws = new WebSocket(getConfig().plus.wsUrl)

    ws.onopen = () => {
      console.log('✅ WebSocket conectado')
    }

    ws.onmessage = (event) => {
      let data: IncomingMessage
      try {
        data = JSON.parse(event.data as string)
      } catch {
        console.error('WS: no JSON')
        return
      }
      try {
        // Se recomienda no llamar a getState() directamente dentro de handlers si puede evitarse,
        // pero se mantiene la lógica original para enfocarse en el problema de credenciales.
        const { conversations, pendingFirstMessage, setPendingFirstMessage } =
          useConversationsStore.getState()
        const isNewConversation = !conversations.some((c) => c.id === data.conversationId)

        if (isNewConversation) {
          if (
            pendingFirstMessage &&
            data.trackingId &&
            data.trackingId === pendingFirstMessage.trackingId
          ) {
            createConversation(data.conversationId, pendingFirstMessage.text)
            addMessage(data.conversationId, {
              id: randomUUID(),
              from: MessageSender.USER,
              text: pendingFirstMessage.text,
              attachments: pendingFirstMessage.attachments?.length
                ? pendingFirstMessage.attachments
                : undefined,
              createdAt: Date.now(),
            } as Message)
            setPendingFirstMessage(null)

            options?.onConversationCreated?.(data.conversationId)
          }
        }
        setAwaitingResponse(data.conversationId, false)
        addMessage(data.conversationId, {
          id: data.parentMessageId, // Asumiendo que este ID se refiere al mensaje del bot en sí.
          from: MessageSender.BOT,
          text: data.text,
          createdAt: Date.now(),
          sources: data.sources,
          failedAttachments: data.failedAttachments,
        } as Message)
      } catch (err) {
        console.error('❌ Error procesando mensaje de WebSocket:', err)
      }
    }

    ws.onclose = (event) => {
      console.log(`❌ WebSocket desconectado. Código: ${event.code}, Razón: ${event.reason}`)
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    socketRef.current = ws
    return ws
  }, [addMessage, createConversation, setAwaitingResponse, options])

  const sendMessage = useCallback(
    async (data: OutgoingMessage) => {
      const ws = connect() // Asegura que el socket esté conectado o intentando conectar

      // 1. Chequeo de autorización explícita
      if (isAuthorized === false) {
        console.error('Usuario no autorizado para enviar mensajes. Operación abortada.')
        return
      }

      // 2. Chequeos relacionados con applicationId y el estado de carga/éxito de Auth0
      if (!applicationId && hasAuth0TokenBeenSuccessfullyUsed && !isLoadingCredentials) {
        // Si Auth0 ya fue exitoso (lo que implica que applicationId se obtuvo en ese momento)
        // pero ahora no hay applicationId y no estamos cargando, es un estado problemático.
        console.error(
          'Application ID no disponible a pesar de un intento de autenticación inicial exitoso. No se puede enviar el mensaje.'
        )
        return
      }

      if (!applicationId && isLoadingCredentials) {
        console.warn(
          'Application ID aún no disponible (configuración inicial/credenciales cargando). Envío de mensaje abortado.'
        )
        return
      }

      // Combinación de chequeos de carga
      if (isLoadingCredentials && (!currentAwsCredentials || !applicationId)) {
        console.warn('Credenciales AWS y/o Application ID aún cargando. Envío de mensaje abortado.')
        return
      }

      // Chequeo de errores en la obtención de credenciales
      if (credentialsError && !currentAwsCredentials) {
        console.error(
          'No se puede enviar mensaje debido a un error en la obtención de credenciales AWS y no hay credenciales válidas en caché:',
          credentialsError.message
        )
        return
      }

      try {
        // Intenta obtener credenciales válidas (esto puede implicar un refresh o un fetch inicial si es necesario)
        const fetchedAwsCredentials = await getValidCredentials()

        if (!fetchedAwsCredentials) {
          // getValidCredentials ya habrá logueado la razón (e.g., not authorized, error)
          // y el estado `isAuthorized` y `error` del provider estarán actualizados.
          console.error('Fallo al obtener credenciales AWS válidas. No se puede enviar el mensaje.')
          if (!isAuthorized) {
            console.error('Detalle: El usuario no está autorizado para el tenant actual.')
          } else if (credentialsError) {
            console.error(
              'Detalle: Hubo un error al obtener credenciales: ',
              credentialsError.message
            )
          }
          return
        }

        // Re-chequear applicationId después de obtener credenciales, ya que `getValidCredentials`
        // podría haber actualizado el estado del provider.
        if (!applicationId) {
          console.error(
            'Application ID es requerido pero no está disponible (incluso después de intentar obtener credenciales). Mensaje no enviado.'
          )
          return
        }

        const payload = JSON.stringify({
          ...data,
          accessKeyId: fetchedAwsCredentials.AccessKeyId,
          secretAccessKey: fetchedAwsCredentials.SecretAccessKey,
          sessionToken: fetchedAwsCredentials.SessionToken,
          applicationId: applicationId, // Usar el applicationId del estado del provider
          email: user?.email,
          tenantId: activeTenant?.id,
        })

        if (ws.readyState === WebSocket.OPEN) {
          ws.send(payload)
        } else {
          console.log('WebSocket no está abierto. Encolando mensaje para enviar al abrirse.')
          const openHandler = () => {
            console.log('WebSocket abierto, enviando mensaje encolado.')
            ws.send(payload)
            ws.removeEventListener('open', openHandler)
          }
          ws.addEventListener('open', openHandler)
        }
      } catch (error) {
        // Error durante getValidCredentials o el envío
        console.error('Error enviando mensaje o en el proceso de obtención de credenciales:', error)
      }
    },
    [
      connect,
      getValidCredentials,
      isLoadingCredentials,
      credentialsError,
      currentAwsCredentials, // Aunque usamos principalmente fetchedAwsCredentials, su cambio puede ser relevante
      applicationId,
      isAuthorized,
      hasAuth0TokenBeenSuccessfullyUsed, // Dependencia actualizada
    ]
  )

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('Desconectando WebSocket...')
      socketRef.current.close()
      socketRef.current = null
    }
  }, [])

  // Efecto para desconectar el WebSocket al desmontar el componente
  // o si cambian condiciones críticas como la autorización o el applicationId.
  useEffect(() => {
    // Si explícitamente no está autorizado o se pierde el applicationId, desconectar.
    // Esto es una decisión de diseño; podría quererse otro comportamiento.
    if (
      isAuthorized === false ||
      (hasAuth0TokenBeenSuccessfullyUsed && !applicationId && !isLoadingCredentials)
    ) {
      console.warn(
        'Desconectando WebSocket debido a no autorizado o pérdida de Application ID post-autorización inicial.'
      )
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [
    disconnect,
    isAuthorized,
    applicationId,
    hasAuth0TokenBeenSuccessfullyUsed,
    isLoadingCredentials,
  ])

  return {
    connect,
    sendMessage,
    disconnect,
    socket: socketRef.current,
  }
}
